import * as React from "react";
import { CSVLink } from "react-csv";
import {
  SpotFxTradeHistorySearchForm,
  SearchParameters,
  SpotFxTradeHistorySearchFormValues,
} from "./SpotFxTradeHistorySearchForm";
import { SpotFxTradeHistoryGrid } from "./SpotFxTradeHistoryGrid";
import {
  ApiResponse,
  TradeSearchResponse,
  FoundTrade,
} from "swan-public-api-client";
import { Spin, Icon, message, Button } from "antd";
import { WrappedComponentProps, injectIntl } from "react-intl";
import { withPapi, PapiClientProps } from "../../Api/PapiHoc";
import { quantityDirectionToStringConverter, tradeSubTypeToStringConverter } from "../../Localisation/Converters";

interface CsvRow {
  Reference: string;
  Market: string;
  SubType: string;
  Direction: string;
  BaseConsideration?: number;
  DeliveryConsideration?: number;
  BookingPrice: number;
  BookingDate: string;
  ValueDate?: string;
}

interface ErrorResponse {
  Message: string;
}

interface State {
  hasSearched: boolean;
  searching: boolean;
  searchResults: FoundTrade[];
  searchFormValues?: SpotFxTradeHistorySearchFormValues;
}

interface Props extends WrappedComponentProps, PapiClientProps {
  tradingAccountKey: string;
}

class SpotFxTradeHistoryCmp extends React.PureComponent<Props, State> {
  public constructor(props: Props) {
    super(props);

    this.state = {
      hasSearched: false,
      searching: false,
      searchResults: [],
    };
  }

  public render() {
    const {
      searchResults,
      searchFormValues,
      hasSearched,
      searching
    } = this.state;
    const {
      intl
    } = this.props;
    const enableDownloadButton = searchResults.length > 0;
    const downloadButton =
      <Button
        type="primary"
        icon="download"
        disabled={!enableDownloadButton}
      >
        {intl.formatMessage({ id: "tradeHistory.downloadButton" })}
      </Button>;

    const tradeHistory = (
      <>
        <SpotFxTradeHistorySearchForm
          onSearch={(searchParameters) => this.onSearch(searchParameters)}
          values={searchFormValues}
          extra={
            enableDownloadButton
              ?
                <CSVLink
                  data={this.convertResultsToCsvData()}
                  filename="axi_trade_history.csv"
                >
                  {downloadButton}
                </CSVLink>
              : downloadButton
          }
        />
        <SpotFxTradeHistoryGrid
          trades={searchResults}
          pagination={{ pageSize: 100 }}
          locale={{
            emptyText: intl.formatMessage({
              id: hasSearched
                ? "tradeHistory.emptySearchResultsEmptyText"
                : "tradeHistory.notYetSearchedEmptyText",
            }),
          }}
        />
      </>
    );

    return searching ? (
      <Spin
        indicator={<Icon type="loading" />}
        tip={intl.formatMessage({ id: "tradeHistory.searching" })}
        delay={100}
      >
        {tradeHistory}
      </Spin>
    ) : (
      <div>{tradeHistory}</div>
    );
  }

  private async onSearch(searchParameters: SearchParameters) {
    this.setState({
      hasSearched: true,
      searching: true,
      searchFormValues: {
        ...searchParameters,
      },
    });
    let searchResults: ApiResponse<TradeSearchResponse>;
    try {
      searchResults = await this.props.papiClient.searchTrades(
        this.props.tradingAccountKey,
        searchParameters.from,
        searchParameters.to,
        searchParameters.marketName,
        searchParameters.direction,
        searchParameters.valueDateFrom,
        searchParameters.valueDateTo,
        searchParameters.tradeSubType
      );
    } catch (error) {
      this.setState({
        searching: false,
        searchResults: [],
      });

      if (error instanceof Error && error.message !== undefined) {
        const errorResponse: ErrorResponse = JSON.parse(error.message);
        const errorMessage = errorResponse.Message;
        message.error(errorMessage);
        return;
      } else {
        // We don't know what this error type is, so we don't know how to handle it
        throw error;
      }
    }

    this.setState({
      searching: false,
      searchResults: searchResults.Content.Trades,
    });
  }

  private convertResultsToCsvData(): CsvRow[] {
    const { intl } = this.props;

    return this.state.searchResults.map(x => {
      return {
        Reference: x.Reference,
        Market: x.MarketName,
        SubType: tradeSubTypeToStringConverter(x.SubType, intl),
        Direction: quantityDirectionToStringConverter(x.OriginalQuantity, intl),
        BaseConsideration: x.Consideration?.BaseConsideration?.Value,
        DeliveryConsideration: x.Consideration?.DeliveryConsideration?.Value,
        BookingPrice: x.BookingPrice,
        BookingDate: `${intl.formatDate(x.BookingTime)} ${intl.formatTime(x.BookingTime)}`,
        ValueDate: intl.formatDate(x.ValueDate)
      }
    });
  }
}

const SpotFxTradeHistoryWithIntl = injectIntl(SpotFxTradeHistoryCmp);

export const SpotFxTradeHistory = withPapi(SpotFxTradeHistoryWithIntl);
